import React, { useContext, useEffect, useRef, useState } from 'react'

import { Modal, Spin, Select, Button, Divider } from 'antd'
import jszip from 'jszip'

import { replaceWithVariables } from '../../../../util'
import { migrate } from './migration-api'
import { buildLocalization } from './migration-localization'
import { DdmOrgsContext } from '../../contexts'
import { NOTICE_URL } from '../../../../constants'
import MigrateModalPanels from './MigrateModalPanels'

import './MigrationModal.css'

// The preact renderer serves each message type from its own entry point, so the
// migration iframes have to point at the same URL the builder previews with (see
// Builder#generateNoticeUrl). usnat and global-cmp both render out of the `us_pm`
// entry and are told apart by query params, and neither has a vendors tab; GDPR
// standard (non-IAB) has its own entries but reuses the TCF components, vendors
// tab included.
const LAYER_SOURCES = {
  'notice-tcf-v2': { notice: 'index.html?type=full', pm: 'privacy-manager/index.html?type=dev', vendors: true },
  'privacy-manager': { notice: 'index.html?type=full', pm: 'privacy-manager/index.html?type=dev', vendors: true },
  'notice_non_iab': { notice: 'notice_non_iab/index.html?type=full', pm: 'privacy_manager_non_iab/index.html?type=dev', vendors: true },
  'privacy_manager_non_iab': { notice: 'notice_non_iab/index.html?type=full', pm: 'privacy_manager_non_iab/index.html?type=dev', vendors: true },
  'usnat_notice': { notice: 'us_pm/index.html?type=dev&is_usnat_notice=true', pm: 'us_pm/index.html?type=dev', vendors: false },
  'us_pm': { notice: 'us_pm/index.html?type=dev&is_usnat_notice=true', pm: 'us_pm/index.html?type=dev', vendors: false },
  'global-cmp-notice': { notice: 'us_pm/index.html?type=dev&is_global_cmp=true&is_global_cmp_notice=true', pm: 'us_pm/index.html?type=dev&is_global_cmp=true', vendors: false },
  'global-cmp-pm': { notice: 'us_pm/index.html?type=dev&is_global_cmp=true&is_global_cmp_notice=true', pm: 'us_pm/index.html?type=dev&is_global_cmp=true', vendors: false },
};

const DEFAULT_LAYER_SOURCES = LAYER_SOURCES['notice-tcf-v2'];

// Native OTT is one message holding every view, rendered from one entry point. Each layer
// is a view of that message, chosen with currentView, and becomes a Didomi CTV widget.
const NATIVE_OTT_LAYERS = [
  { layerRole: 'notice', view: 'HomeView' },
  { layerRole: 'preferences', view: 'CategoriesView' },
  { layerRole: 'vendors', view: 'VendorsView' },
];

export const MigrateModal = ({
  notice,
  pm,
  messageSubcategory,
  messageCategory,
  siteId,
  visible,
  closeModal,
}) => {
  const isNativeOtt = messageSubcategory === 'native-ott';
  const layerSources = LAYER_SOURCES[messageSubcategory] || DEFAULT_LAYER_SOURCES;
  // A native OTT message carries its preferences and vendors views itself.
  const hasPreferencesLayer = isNativeOtt || Boolean(pm);
  // Only the TCF Privacy Manager exposes a separate vendors tab; every other
  // supported PM migrates as a single preferences layer.
  const hasVendorsLayer = isNativeOtt || (Boolean(pm) && layerSources.vendors);
  const ddmOrgs = useContext(DdmOrgsContext)

  const [selectedOrg, setSelectedOrg] = useState(ddmOrgs[0]?.ddm_organization_id)
  const [status, setStatus] = useState('working')
  const [layerData, setLayerData] = useState([])
  const [iframesStatus, setIframesStatus] = useState({ notice: true, preferences: !!pm, vendors: hasVendorsLayer })
  const [preflightFailureMessage, setPreflightFailureMessage] = useState('')

  const noticeIframeRef = useRef()
  const preferencesRef = useRef()
  const vendorsRef = useRef()
  const timeoutIdsRef = useRef({})

  useEffect(() => {
    const migrationHandler = async (event) => {
      if (event.data.type === 'SCREENSHOT_DATA') {
        const { layerRole, html, css, screenshot } = event.data

        clearTimeout(timeoutIdsRef.current[layerRole])

        setLayerData(prevLayerData => [
          ...prevLayerData,
          {
            layerRole,
            layerType: isNativeOtt
              ? 'fullscreen-layer'
              : layerRole === 'notice' ? getType(notice) : getType(pm),
            screenshot,
            html,
            css,
          }
        ])
      }
    }

    window.addEventListener('message', migrationHandler)

    return () => window.removeEventListener('message', migrationHandler)
  }, [])

  useEffect(() => {
    let layerLimit = 0
    if (notice) layerLimit++
    if (hasPreferencesLayer) layerLimit++
    if (hasVendorsLayer) layerLimit++

    if (layerData.length === layerLimit) {
      setStatus('waiting')
    }
  }, [layerData.length])

  const sendLayers = async () => {
    if(status == 'waiting') {
      setStatus('confirming')
      return
    } else {
      setStatus('sending')

      // Extracted from the message records rather than the rendered iframes: the
      // screenshot only ever shows one language, which is the limitation this
      // works around. Omitted when the message is single-language.
      const localization = buildLocalization([notice, pm], { replaceWithVariables })

      const { success, error } = await migrate(
        selectedOrg,
        layerData,
        notice ? notice.description : pm.description,
        localization,
        notice ? notice.id : pm.id,
        isNativeOtt ? 'ctv' : undefined,
      )
      if (success) {
        setStatus('successful')
      } else if (error.preflightFailed) {
        setStatus('failure')
        setPreflightFailureMessage(error.preflightFailed)
      } else if (error.internalServerError) {
        setStatus('server-error')
      }
    }
  }

  const zipFiles = async () => {
    const zip = new jszip()

    const preferencesFolder = zip.folder('preferences')
    const vendorsFolder = hasVendorsLayer ? zip.folder('vendors') : null

    if (notice) {
      const noticeFolder = zip.folder('notice')
      const noticeFiles = layerData.find(({ layerRole }) => layerRole === 'notice')

      noticeFolder.file('notice.html', noticeFiles.html)
      noticeFolder.file('notice.css', noticeFiles.css)
      noticeFolder.file('notice.png', noticeFiles.screenshot, { base64: true, })
    }

    if (hasPreferencesLayer) {
      const preferencesFiles = layerData.find(({ layerRole }) => layerRole === 'preferences')
      const vendorsFiles = layerData.find(({ layerRole }) => layerRole === 'vendors')

      preferencesFolder.file('preferences.html', preferencesFiles.html)
      preferencesFolder.file('preferences.css', preferencesFiles.css)
      preferencesFolder.file('preferences.png', preferencesFiles.screenshot, { base64: true, })

      if (vendorsFolder && vendorsFiles) {
        vendorsFolder.file('vendors.html', vendorsFiles.html)
        vendorsFolder.file('vendors.css', vendorsFiles.css)
        vendorsFolder.file('vendors.png', vendorsFiles.screenshot, { base64: true, })
      }
    }

    const zipBlob = await zip.generateAsync({ type: 'blob', })

    const url = URL.createObjectURL(zipBlob)
    const link = document.createElement('a')

    link.href = url
    link.download = 'migration.zip'

    document.body.appendChild(link)
    link.click()
    link.remove()

    URL.revokeObjectURL(url)
  }

  // Signal to the parent whether the migration actually succeeded, so it can
  // record the last-migrated date. Both the modal's cancel affordance and the
  // panel buttons route through here.
  const handleClose = () => closeModal(status === 'successful')

  const getType = (msg) => {
    const type = JSON.parse(msg.message_settings).type || 'modal'

    let layerType
    switch (type) {
      case 'top':
        layerType = 'top-banner-layer'
        break
      case 'bottom':
        layerType = 'bottom-banner-layer'
        break
      case 'inline':
        layerType = 'embedded-layer'
        break
      default:
        layerType = 'popin-layer'
    }

    return layerType
  }

  const startMigration = ({ currentTarget: iframe }, { message_json, variables }, layerRole) => {
    const msgJSON = JSON.parse(message_json)
    const component = msgJSON.siblings || [msgJSON]

    iframe.contentWindow.postMessage(JSON.stringify({
      components: replaceWithVariables(component, variables),
      current: [0],
      siteId,
      screenshot: true,
    }), '*')

    armScreenshotTimeout(layerRole)
  }

  // Native OTT takes the whole message as `root`, plus the view to show.
  const startNativeOttMigration = ({ currentTarget: iframe }, { message_json, variables }, { layerRole, view }) => {
    const [root] = replaceWithVariables([JSON.parse(message_json)], variables)

    iframe.contentWindow.postMessage(JSON.stringify({
      root,
      components: root.children,
      currentView: view,
      siteId,
      screenshot: true,
    }), '*')

    armScreenshotTimeout(layerRole)
  }

  const armScreenshotTimeout = (layerRole) => {
    timeoutIdsRef.current = {
      ...timeoutIdsRef.current,
      [layerRole]: setTimeout(() => setStatus('screenshot-time-out'), 5000)
    }
  }

  return (
    <Modal
      centered
      visible={visible}
      onCancel={handleClose}
      title={null}
      footer={null}
      wrapClassName='scenario-modal-wrapper'
    >
      <div
        className='scenario-view-message-wrapper migration-modal'
      >
        <div class='iframe-overlay'>
          <MigrateModalPanels
            status={status}
            closeModal={handleClose}
            ddmOrgs={ddmOrgs}
            selectedOrg={selectedOrg}
            selectOrg={setSelectedOrg}
            sendLayers={sendLayers}
            preflightFailureMessage={preflightFailureMessage}
            zipFiles={zipFiles}
            noticeName={notice?.description}
            pmName={isNativeOtt ? notice?.description : pm?.description}
          />
        </div>
        {isNativeOtt && notice && NATIVE_OTT_LAYERS.map((layer) => (
          <iframe
            key={layer.layerRole}
            className='migration-iframe migration-iframe-ott'
            onLoad={e => startNativeOttMigration(e, notice, layer)}
            src={`${NOTICE_URL}/native-ott/index.html?type=dev${messageCategory === 'ccpa' ? '&is_ccpa=true' : ''}&site_id=${siteId}&screenshot=true`}
          />
        ))}
        {!isNativeOtt && notice && (
          <iframe
            className='migration-iframe'
            ref={noticeIframeRef}
            onLoad={e => startMigration(e, notice, 'notice')}
            src={`${NOTICE_URL}/${layerSources.notice}&site_id=${siteId}&screenshot=true`}
          />
        )}
        {!isNativeOtt && pm && (
          <>
            <iframe
              className='migration-iframe'
              ref={preferencesRef}
              onLoad={e => startMigration(e, pm, 'preferences')}
              src={`${NOTICE_URL}/${layerSources.pm}&site_id=${siteId}&screenshot=true`}
            />

            {hasVendorsLayer && (
              <iframe
                className='migration-iframe'
                ref={vendorsRef}
                onLoad={e => startMigration(e, pm, 'vendors')}
                src={`${NOTICE_URL}/${layerSources.pm}&site_id=${siteId}&pmTab=vendors&screenshot=true`}
              />
            )}
          </>
        )}
      </div>
    </Modal >
  )
};